import { CATEGORY_ID, ID, PREHEADER_PROP, HEADLINE_PROP } from '../constants';

const defaultProps = [ID, 'name'];
const categoryProps = 'id publishedListingPage{id, url}';

const checkProps = props =>
  !!(props && Object.keys(props).filter(prop => prop === CATEGORY_ID).length);

const buildPropsQuery = (entitySchema, extraProps = [], cardOptions = null, linkProps = []) => {
  const parsedSchema = entitySchema.getEntitySchema ? entitySchema.getEntitySchema : entitySchema;
  const extraPropsHaveCategory = !!extraProps.filter(prop => prop.includes('category.')).length;
  const { typeBasedProps, shouldAddCategoryProps } = getTypeBaseProps(
    parsedSchema,
    cardOptions,
    extraPropsHaveCategory
  );

  const allProps = [...typeBasedProps, ...extraProps];
  const uniqueProps = [...new Set([...allProps])];
  const basicProps = [];
  const nestedProps = {};
  uniqueProps.forEach(prop => {
    if (prop) {
      if (!prop.includes('.')) {
        basicProps.push(prop);
        return;
      }
      const [base, nested] = prop.split('.');
      if (!nestedProps[base]) nestedProps[base] = [nested];
      else nestedProps[base].push(nested);
    }
  });

  const complexProps = buildComplexProps(
    shouldAddCategoryProps,
    nestedProps,
    parsedSchema,
    linkProps
  );

  return [...basicProps, ...complexProps].join(',');
};

const buildComplexProps = (
  shouldAddCategoryProps,
  props,
  { relations = [], dynamicProperties = {}, properties = {} },
  linkProps
) =>
  Object.keys(props).map(base => {
    const nestedProps = props[base];
    const hasLink = !!linkProps.find(linkProp => linkProp.includes(base));
    const matchingProperty = !!properties[base];
    const matchingRelation = relations.find(({ localField }) => localField === base);
    const matchingDynamicProp = Object.keys(dynamicProperties).find(
      dynamicKey => dynamicKey === base
    );

    if (matchingRelation && !nestedProps.includes('id')) nestedProps.push('id');
    if (hasLink && (!!matchingProperty || !!matchingRelation || !!matchingDynamicProp))
      nestedProps.push('url');
    const jointNestedProps = nestedProps.join(',');
    if (base === 'category' && shouldAddCategoryProps)
      return `${base}{${jointNestedProps}, ${categoryProps}}`;
    if (base.includes('published')) {
      return `${base}{${jointNestedProps},url}`;
    }
    return `${base}{${jointNestedProps}}`;
  });

const getTypeBaseProps = (entitySchema, cardOptions, extraPropsHaveCategory) => {
  const isCard = !!cardOptions;
  const { displayCategory = true, displayThumbnail = true } = cardOptions || {};

  const shouldAddCategoryProps =
    (isCard && displayCategory) || (!isCard && !extraPropsHaveCategory);

  if (!isCard) return { typeBasedProps: [ID], shouldAddCategoryProps };

  const { interfaces, properties, dynamicProperties, relations } = entitySchema;

  const isContent = !!interfaces.includes('content/content');
  const hasCategory = checkProps(properties) || checkProps(dynamicProperties);
  const hasPreheader = properties[PREHEADER_PROP];

  const typeBasedProps = [...defaultProps];

  typeBasedProps.push(...getCategoyProps(shouldAddCategoryProps, hasCategory, hasPreheader));
  typeBasedProps.push(...getContentProps(isContent));

  if (properties.url || dynamicProperties.url) typeBasedProps.push('url');

  if (displayThumbnail && relations.find(({ localField }) => localField === 'image')) {
    typeBasedProps.push('image.id', 'image.url', 'image.data');
  }

  return { typeBasedProps, shouldAddCategoryProps };
};

const getContentProps = isContent => (isContent ? ['url', 'sponsored', 'featured'] : []);

const getCategoyProps = (shouldAddCategoryProps, hasCategory, hasPreheader) => {
  if (!shouldAddCategoryProps) return [];
  const props = [];
  if (hasCategory)
    props.push('category.name', 'category.listingPageId', 'category.listingPageEntity');

  if (hasPreheader) {
    props.push(`${PREHEADER_PROP}{name}`);
    props.push(HEADLINE_PROP);
  }

  return props;
};

export default buildPropsQuery;
